import { Box, Stack, Typography } from "@mui/joy";

import { IconButton } from "../../shared";
import { formatDate, getDaysInMonth, isToday } from "./utils";
import type { CalendarEvent } from "./TimeGridView";
import { EventBlock } from "./EventBlock";

const GRID_BORDER = "rgba(128, 128, 128, 0.18)";

interface MonthViewProps {
    currentDate: Date;
    selectedDate: Date;
    events: CalendarEvent[];
    onDateClick: (date: Date) => void;
    onEventClick: (event: CalendarEvent, e: React.MouseEvent) => void;
}

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function MonthView({
    currentDate,
    selectedDate,
    events,
    onDateClick,
    onEventClick,
}: MonthViewProps) {
    const calendarDays = getDaysInMonth(currentDate);

    const getEventsForDate = (date: string) => events.filter((event) => event.date === date);

    return (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    borderBottom: "1px solid",
                    borderColor: GRID_BORDER,
                    flexShrink: 0,
                }}
            >
                {daysOfWeek.map((day) => (
                    <Typography
                        key={day}
                        level="body-xs"
                        sx={{
                            ml: 1.5,
                            my: 1,
                            textTransform: "uppercase",
                            letterSpacing: "0.4px",
                            fontWeight: 600,
                            color: "text.tertiary",
                            borderRight: "1px solid",
                            borderColor: GRID_BORDER,
                        }}
                    >
                        {day}
                    </Typography>
                ))}
            </Box>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, 1fr)",
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto",
                }}
            >
                {calendarDays.map((date, index) => {
                    const dateStr = formatDate(date);
                    const dayEvents = getEventsForDate(dateStr);
                    const inMonth =
                        date.getMonth() === currentDate.getMonth() &&
                        date.getFullYear() === currentDate.getFullYear();
                    const today = isToday(date);
                    const selected = date.toDateString() === selectedDate.toDateString();

                    return (
                        <Box
                            key={index}
                            onClick={() => onDateClick(date)}
                            sx={{
                                borderRight: "1px solid",
                                borderBottom: "1px solid",
                                borderColor: GRID_BORDER,
                                cursor: "pointer",
                                minHeight: 110,
                                display: "flex",
                                flexDirection: "column",
                                position: "relative",
                                bgcolor: today
                                    ? "rgba(var(--standard-palette-primary-mainChannel) / 0.06)"
                                    : "transparent",
                                "&:hover": {
                                    bgcolor: "rgba(128, 128, 128, 0.04)",
                                    "& .hover-add-button": { opacity: 1 },
                                },
                                "&:nth-of-type(7n)": { borderRight: "none" },
                            }}
                        >
                            {inMonth && (
                                <Box
                                    className="hover-add-button"
                                    sx={{
                                        position: "absolute",
                                        top: 6,
                                        right: 6,
                                        opacity: 0,
                                        transition: "opacity 0.15s ease",
                                        zIndex: 1,
                                    }}
                                >
                                    <IconButton
                                        size="sm"
                                        variant="solid"
                                        color="primary"
                                        sx={{
                                            width: 22,
                                            height: 22,
                                            minWidth: 22,
                                            borderRadius: "4px",
                                            boxShadow: "none",
                                        }}
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            onDateClick(date);
                                        }}
                                    >
                                        +
                                    </IconButton>
                                </Box>
                            )}

                            <Box sx={{ p: 1, flex: 1, display: "flex", flexDirection: "column" }}>
                                <Box
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        mb: 0.75,
                                        opacity: inMonth ? 1 : 0.35,
                                        ...(today && {
                                            bgcolor: "primary.solidBg",
                                            color: "primary.solidColor",
                                        }),
                                        ...(selected &&
                                            !today && {
                                                border: "1px solid",
                                                borderColor: "primary.outlinedBorder",
                                            }),
                                    }}
                                >
                                    <Typography
                                        level="body-sm"
                                        sx={{ fontWeight: 600, color: "inherit" }}
                                    >
                                        {date.getDate()}
                                    </Typography>
                                </Box>

                                <Stack spacing={0.5} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                                    {dayEvents.slice(0, 3).map((event) => (
                                        <EventBlock
                                            key={event.id}
                                            event={event}
                                            compact
                                            onClick={onEventClick}
                                        />
                                    ))}
                                    {dayEvents.length > 3 && (
                                        <Typography
                                            level="body-xs"
                                            sx={{ color: "text.tertiary", px: 0.5 }}
                                        >
                                            +{dayEvents.length - 3} more
                                        </Typography>
                                    )}
                                </Stack>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
